import React, { useCallback, useEffect, useState } from "react";
import { StaticRouter } from "react-router-dom/server";
import { Fiche } from "../Fiche/Fiche";
import { Filter } from "./Filter/Filter";
import { defaultDateRange } from "./Filter/Filters";
import { Popup } from "../Popup/Popup";
import { debounce } from "lodash";
import { Button } from "../Button/Button";
import { Loading } from "./Loading/Loading";
import { useMemo } from "react";

const navigate = (path) => {
  if (
    window &&
    window.location &&
    window.location.pathname &&
    window.location.pathname !== path
  ) {
    // Opening or closing a fiche only changes the path; the filter selections in the query string stay.
    window.history.pushState(
      {},
      "",
      `${path}${window.location.search}${window.location.hash}`
    );
  }
};

const getCurrentPath = () => {
  let curPath = `${window.location.pathname.split("/fiche/")[0]}`;
  if (!curPath.endsWith("/")) {
    curPath = curPath + "/";
  }
  return curPath;
};

// Derives the URL path segment for a fiche type and matches it back on reload.
// Symmetric by construction (segment written === segment matched), so popup deep links
// round-trip for every type. The regex is case-insensitive so "dynamicFiche" (where "fiche"
// is a capitalised suffix) yields "dynamic", matching the intent for the existing fiche{Suffix} types too.
const typeToSegment = (type) => (type ? type.replace(/fiche/gi, "").toLowerCase() : "");

// Query-string names a filter's prefilter values can arrive under, most readable first. `prefilterKey` is the
// filter's own readable name as published by the server (which falls back to `prefilter.<fieldKey>` itself when
// the name is empty, reserved or ambiguous); the remaining names keep links made before readable keys working.
const prefilterNames = (f) => {
  const names = [];
  if (f.prefilterKey) names.push(f.prefilterKey);
  const fieldKey = f.type === 7 ? f.propertyStart : f.property;
  if (fieldKey) names.push(`prefilter.${fieldKey}`);
  if (f.property === "__ficheType") names.push("prefilter.ficheType");
  return [...new Set(names)];
};

// Merges the prefilters configured on the fiche list block with the ones in the URL. A parameter present in the
// URL replaces the block's values for that key entirely, so a prefilter link to the page wins over the block
// without wiping the keys the link does not mention.
const effectiveParams = (blockPrefilters, search) => {
  const params = new URLSearchParams(blockPrefilters ?? "");
  const fromUrl = new URLSearchParams(search ?? "");

  [...new Set(fromUrl.keys())].forEach((key) => {
    params.delete(key);
    fromUrl.getAll(key).forEach((value) => params.append(key, value));
  });
  return params;
};

// Seeds filter selections on mount, from the block's own prefilters and from the querystring. Selections remain
// editable afterwards unless the filter options are hidden.
// Value filters:  ?naam=Wout                        (repeatable for checkboxes)
// Fiche type:     ?ficheType=<typeName>
// Date range:     ?periode.from=YYYY-MM-DD&periode.to=YYYY-MM-DD
const applyPrefilters = (filter, blockPrefilters) => {
  if (
    !filter ||
    !filter.filters ||
    typeof window === "undefined" ||
    !window.location
  ) {
    return filter;
  }
  const params = effectiveParams(blockPrefilters, window.location.search);
  filter.filters.forEach((f) => {
    const names = prefilterNames(f);

    if (f.type === 7) {
      // Date range keys off the filter's name with .from/.to suffixes. A present-but-empty bound is a bound the
      // sender deliberately cleared, and stays open instead of falling back to the default range.
      names.forEach((name) => {
        const from = params.get(`${name}.from`);
        const to = params.get(`${name}.to`);
        if (from !== null) f.dateStartValue = from;
        if (to !== null) f.dateEndValue = to;
      });
      return;
    }

    let values = [];
    names.forEach((name) => {
      values = values.concat(params.getAll(name));
    });
    if (values.length === 0) return;

    switch (f.type) {
      case 1: // Options (checkboxes / radio) + fiche type
        f.values = [...new Set([...(f.values || []), ...values])];
        break;
      case 5: // Dropdown
        f.value = values[0];
        break;
      case 8: // True/False
        f.trueFalseValue = values[0] === "true" || values[0] === "1";
        break;
      default:
        break;
    }
  });
  return filter;
};

// Normalises a stored bound to YYYY-MM-DD. Bounds reach this in three shapes: the picker's own format, the
// server's echo with a time, and year 1 for an open bound.
const dateParam = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  if (!match || match[1] === "0001") return "";
  return `${match[1]}-${match[2]}-${match[3]}`;
};

// The mirror image of applyPrefilters: publishes the visitor's own selections under the same readable keys, so
// a copied URL reproduces the list for whoever receives it. Written with replaceState, so filtering does not
// fill the back button. Parameters this list does not own — popup deep links (?type=&alias=) among them — are
// left untouched. Values a visitor never changed are left out, which keeps an untouched page's URL clean.
const publishPrefilters = (filter) => {
  if (
    !filter ||
    !filter.filters ||
    typeof window === "undefined" ||
    !window.location ||
    !window.history
  ) {
    return;
  }

  // Only filters the server assigned a readable key to take part in this contract. Every other fiche list keeps
  // its URL untouched: those filters have a `property` but no `prefilterKey`, and publishing them would put
  // parameters on pages that never read them back.
  if (!filter.filters.some((f) => f.prefilterKey)) return;

  const params = new URLSearchParams(window.location.search);
  const untouchedRange = defaultDateRange();

  filter.filters.forEach((f) => {
    if (!f.prefilterKey) return;

    const names = prefilterNames(f);
    if (names.length === 0) return;

    // Every name this filter can be read under is cleared first, including the older prefilter.<key> forms,
    // so a link made before readable keys existed does not keep a stale duplicate alongside the new one.
    names.forEach((name) => {
      params.delete(name);
      params.delete(`${name}.from`);
      params.delete(`${name}.to`);
    });

    const key = names[0];
    switch (f.type) {
      case 7: {
        const from = dateParam(f.dateStartValue);
        const to = dateParam(f.dateEndValue);
        if (from === untouchedRange.from && to === untouchedRange.to) break;
        // Both bounds are written once the range differs from the default, a cleared one as an empty value, so
        // the recipient of the link gets the same open range instead of the default.
        params.set(`${key}.from`, from);
        params.set(`${key}.to`, to);
        break;
      }
      case 1:
        (f.values || []).filter(Boolean).forEach((value) => params.append(key, value));
        break;
      case 5:
        if (f.value) params.set(key, f.value);
        break;
      case 8:
        if (f.trueFalseValue) params.set(key, "true");
        break;
      default:
        break;
    }
  });

  const query = params.toString();
  const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) {
    window.history.replaceState({}, "", next);
  }
};

export const FicheLijst = (props) => {
  const apiUrl = "/umbraco/api/";
  //#region  states

  const [ficheData, setFicheData] = useState({
    folderId: null,
    themas: [],
    fiches: [],
    type: props.type || null,
    totalCount: 0,
    filter: null,
    pageId: null,
    culture: null,
  });

  const [rendered, setRendered] = useState(0);
  const [currentPopupData, setCurrentPopupData] = useState(null);

  const [pathName, setPathName] = useState("");
  const [ficheTypeClassName, setFicheTypeClassName] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [loadAnimations, setLoadAnimations] = useState(true);

  //#endregion states

  //#region functions

  const startLoading = () => {
    setIsLoading(true);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  //Clear popup state
  const closePopup = () => {
    setCurrentPopupData(null);
    navigate(getCurrentPath());
    document.querySelector("html").classList.remove("u-noscroll");
  };

  const checkDetailView = () => {
    if (
      props.urlPopup &&
      ficheData.type &&
      typeToSegment(ficheData.type) === (props.urlPopup.type || "").toLowerCase()
    ) {
      if (props.urlPopup.alias) {
        openPopup(props.urlPopup.alias);
        props.urlPopup = null;
        setLoadAnimations(false);
      }
    } else if (window.location.href.includes(`/fiche/`) && ficheData.type) {
      var type = window.location.href.split("/fiche/")[1].split("/")[0];
      var alias = window.location.href.split("/fiche/")[1].split("/")[1];
      if (
        type &&
        alias &&
        typeToSegment(ficheData.type) === type.toLowerCase()
      ) {
        openPopup(alias);
        setLoadAnimations(false);
      } else {
        closePopup();
      }
    } else if (currentPopupData) {
      closePopup();
    }
  };

  //#endregion functions

  //#region callbacks
  const printComponentToPdf = useCallback(async (urlSegment) => {
    let filename = "ficheDetail";

    fetch(
      `${apiUrl}Fiche/GetDetailPdf/?folderId=${props.folderId}&urlSegment=${urlSegment}`
    )
      .then((response) => {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const responseFilename = response.headers
          .get("content-disposition")
          .match(filenameRegex)[1];
        if (responseFilename) filename = responseFilename;

        return response.blob();
      })
      .then((blob) => {
        const href = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", filename); //or any other extension
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err) => {
        return Promise.reject({ Error: "Something Went Wrong", err });
      });
  }, []);

  // Loads fiche data /w debounce
  const loadFicheData = useCallback(
    debounce((ficheData) => {
      //Load Fiche data
      try {
        var ficheDataToSend = { ...ficheData };
        ficheDataToSend.fiches = [];
        fetch(`${apiUrl}Fiche/GetFiches`, {
          method: "POST",
          headers: new Headers({ "Content-Type": "application/json" }),
          body: JSON.stringify(ficheDataToSend),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data && data.filter && data.filter.page === 0) {
              if (
                JSON.stringify(ficheData.fiches) !==
                  JSON.stringify(data.fiches) ||
                ficheData.totalCount !== data.totalCount
              ) {
                setFicheData({
                  ...data,
                  pageId: props.pageId,
                  culture: props.culture,
                });
              }

              stopLoading();
            } else {
              var oldFicheData = Object.assign({}, ficheData);
              oldFicheData.fiches = oldFicheData.fiches.concat(data.fiches);
              oldFicheData.totalCount = data.totalCount;
              oldFicheData.filter = data.filter;
              oldFicheData.titleSingle = data.titleSingle;
              oldFicheData.titlePlural = data.titlePlural;
              oldFicheData.pageId = props.pageId;
              oldFicheData.culture = props.culture;
              setFicheData(oldFicheData);
            }
          })
          .catch(() => {
            stopLoading();
          });
      } catch (error) {
      }
    }, 500),
    []
  );

  //Get detail data & show popup
  const openPopup = useCallback(
    (urlSegment) => {
      //get fiche  data by id
      try {
        fetch(
          `${apiUrl}Fiche/GetFicheDetails?folderId=${props.folderId}&urlSegment=${urlSegment}`
        )
          .then((response) => response.json())
          .then((data) => {
            data.detailContactAddress = props.contactEmailAddress;
            setCurrentPopupData(data);

            let urlType = typeToSegment(ficheData.type);
            navigate(
              getCurrentPath() +
                props.url +
                urlType +
                "/" +
                data.urlSegment +
                "/"
            );
            document.querySelector("html").classList.add("u-noscroll");
          });
      } catch (error) {
      }
    },
    [pathName, ficheData.type]
  );

  //#endregion callbacks

  //#region useEffects

  //On fiche type & parameter render popup
  useEffect(() => {
    if (ficheData.type) {
      checkDetailView();
    }
  }, [ficheData.type]);

  // useEffect(() => {
  //     window.onpopstate = () => checkDetailView();
  // }, [ficheData.type, currentPopupData]);

  //Load filter data
  useEffect(() => {
    const loadFilterData = () => {
      try {
        fetch(
          `${apiUrl}Fiche/GetFilter?folderId=${props.folderId}&culture=${props.culture}&pageId=${props.pageId}`
        )
          .then((response) => response.json())
          .then((data) => {
            applyPrefilters(data, props.prefilters);
            setFicheData((prev) => {
              return {
                ...prev,
                filter: data,
                folderId: props.folderId,
                themas: props.themas,
                categories: props.categories,
                type: props.type,
                url: props.url,
                pageId: props.pageId,
                culture: props.culture,
              };
            });
          });
      } catch (error) {
      }
    };
    if (rendered === 0) {
      loadFilterData();
      setRendered(rendered + 1);
      let curPath = getCurrentPath();

      setPathName(curPath);
    }
  }, [rendered]);

  //On filter change update fiche data and the shareable URL
  useEffect(() => {
    if (ficheData.filter) {
      // startLoading();
      publishPrefilters(ficheData.filter);
      loadFicheData(ficheData);
    }
  }, [JSON.stringify(ficheData.filter)]);

  const currentPopup = useMemo(() => {
    var data = currentPopupData;
    return (
      <Popup
        data={data}
        printComponentToPdf={printComponentToPdf}
        pathName={pathName}
        type={ficheData.type}
        dictionary={props.dictionary}
        closePopupHandler={closePopup}
      />
    );
  }, [JSON.stringify(currentPopupData), ficheData.type, props.dictionary]);

  //#endregion useEffects
  return (
    <StaticRouter>
      {
        //Add col class to fiche list if amountPerRow is set
        <div
          data-aos={loadAnimations ? "fade-up" : undefined}
          className={`fiche-lijst ${ficheData.type} ${
            ficheData.filter && ficheData.filter.amountPerRow == 4
              ? "c-fichelijst-col-3"
              : ficheData.filter && ficheData.filter.amountPerRow == 5
              ? "c-fichelijst-col-2"
              : ""
          } ${props.bottomSpacing ? props.bottomSpacing : ""} ${
            props.hideFilters ? "filters-hidden" : ""
          }`}
        >
          {
            <div className="c-fichelijst-body">
              {
                //Check if filter is available
                //Check if themas filter is set (only for fiche publications list)
                ficheData.filter &&
                  ((ficheData.themas && ficheData.themas.length == 0) ||
                    !ficheData.themas) && (
                    <Filter
                      startLoading={startLoading}
                      setFicheData={setFicheData}
                      dictionary={props.dictionary}
                      ficheData={ficheData}
                      amount={ficheData.totalCount}
                      hideFilters={props.hideFilters}
                    />
                  )
              }
              {!isLoading ? (
                //Render fiches
                <Fiches
                  dictionary={props.dictionary}
                  ficheData={ficheData}
                  setFilterHandler={setFicheData}
                  ficheTypeClassName={ficheTypeClassName}
                  openPopup={openPopup}
                  logoFiches={props.logoFiches}
                />
              ) : (
                <Loading />
              )}
            </div>
          }
        </div>
      }
      {/* Render popup if state contains popup data */}
      {currentPopupData && currentPopup}
    </StaticRouter>
  );
};

//Separated fiches component
const Fiches = (props) => {
  const fiches = useMemo(() => {
    let newFiches = [];

    if (
      props.ficheData &&
      props.ficheData.fiches &&
      props.ficheData.fiches.length > 0
    ) {
      props.ficheData.fiches.forEach((fiche, key) => {
        newFiches.push(
          <Fiche
            dictionary={props.dictionary}
            key={"Fiche_" + key}
            data={fiche}
            ficheType={props.ficheData.type}
            openPopup={props.openPopup}
            isLogoFiche={props.logoFiches}
          />
        );
      });

      return newFiches;
    } else {
      return <h5>{props.dictionary.notFound}</h5>;
    }
  }, [JSON.stringify(props.ficheData.fiches)]);

  return (
    <div className="c-section__body">
      <div className={props.ficheTypeClassName}>
        <div
          className={`c-fiche-wrap ${
            props.logoFiches ? "c-fiche-wrap--logo" : ""
          }  u-clear`}
        >
          {fiches}
        </div>
        {fiches.length < props.ficheData.totalCount && (
          <div className="status ng-scope">
            <div className="buttonContainer">
              <Button
                buttonType={"secondary"}
                label={props.dictionary.loadMore}
                onClick={() =>
                  props.setFilterHandler((prev) => {
                    let filter = { ...prev.filter };
                    filter.page = filter.page + 1;
                    return {
                      ...prev,
                      filter: filter,
                    };
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
